/**  @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Boundary } from '@floating-ui/react';
import classNames from 'classnames';

import { breakpoints } from '@coursera/cds-core';

import FullscreenExitSvg from 'bundles/video-player/assets/svg/FullscreenExitSvg';
import FullscreenSvg from 'bundles/video-player/assets/svg/FullscreenSvg';
import ModernTooltipWrapper from 'bundles/video-player/components/ModernTooltipWrapper';
import CrossBrowserFullscreenUtils from 'bundles/video-player/utils/CrossBrowserFullscreenUtils';

import _t from 'i18n!nls/video-player';

const styles = {
  fullscreenToggleTooltipWrapper: css`
    ${breakpoints.down('xs')} {
      height: 30px;
      display: flex;
      align-items: center;
    }

    .rc-FullscreenToggle {
      color: #dedede;
    }
  `,
  modernFullScreenToggle: css`
    /* 
    Overriding videojs classname styling for ease of implementing the code for the experiment. The 'important' flag won't be
    needed once the experiment is rolled out and className are removed
    */
    margin-right: var(--cds-spacing-100) !important;
  `,
};

type Props = {
  videoBoundaryElement?: Boundary;
  onChangeFullScreen: (isFullscreen: boolean) => void;
};

function FullscreenToggle({ videoBoundaryElement, onChangeFullScreen }: Props) {
  const isFullscreen = CrossBrowserFullscreenUtils.isFullScreen();

  const label = isFullscreen ? _t('Exit full Screen') : _t('Full Screen');

  const fullscreenSvg = isFullscreen ? (
    <FullscreenExitSvg width={28} height={28} />
  ) : (
    <FullscreenSvg width={28} height={28} />
  );

  // NOTE: continuing to use the VJS skin styles for now
  // once we get stuff into React we can migrate the skin
  return (
    <ModernTooltipWrapper
      className="fullscreen-toggle-tooltip-wrapper"
      placement="top"
      message={label}
      keyboardShortcut="f"
      css={styles.fullscreenToggleTooltipWrapper}
      videoBoundaryElement={videoBoundaryElement}
    >
      <button
        type="button"
        onClick={() => {
          onChangeFullScreen(isFullscreen);
        }}
        className={classNames(
          'rc-FullscreenToggle',
          'vjs-button',
          'c-vjs-button',
          'vjs-control',
          'c-video-control',
          'c-fullscreen-control',
          'horizontal-box',
          'align-items-absolute-center'
        )}
        aria-label={label}
        aria-expanded={isFullscreen}
        css={styles.modernFullScreenToggle}
      >
        <React.Fragment>{fullscreenSvg}</React.Fragment>

        <span className="vjs-control-text"> {label} </span>
      </button>
    </ModernTooltipWrapper>
  );
}
export default FullscreenToggle;
